import { getSuperContaContext } from './superconta-context';

export const SUPERCONTA_CHAT_STORAGE_KEY = 'profase-crm-superconta-chat';
export const OPEN_SUPERCONTA_CHAT_EVENT = 'profase:open-superconta-chat';
export const ADD_SUPERCONTA_CHAT_MESSAGE_EVENT = 'profase:add-superconta-chat-message';

export function openSuperContaChat(): void {
  window.dispatchEvent(new CustomEvent(OPEN_SUPERCONTA_CHAT_EVENT));
}

export function addMessageToChat(text: unknown): void {
  const trimmed = text == null ? '' : String(text).trim();
  if (!trimmed) return;
  window.dispatchEvent(new CustomEvent(ADD_SUPERCONTA_CHAT_MESSAGE_EVENT, { detail: { text: trimmed } }));
}

if (typeof window !== 'undefined') {
  window.addMessageToChat = addMessageToChat;
}

export const CHAT_MSG_TO_PARAM = 'chat-msg-to';

export function parseChatMsgToFromHash(hash: string): string | null {
  const q = hash.indexOf('?');
  if (q < 0) return null;
  const value = new URLSearchParams(hash.slice(q + 1)).get(CHAT_MSG_TO_PARAM);
  const trimmed = value?.trim();
  return trimmed || null;
}

export function hashWithoutChatMsgTo(hash: string): string {
  const q = hash.indexOf('?');
  if (q < 0) return hash;
  const path = hash.slice(0, q);
  const params = new URLSearchParams(hash.slice(q + 1));
  if (!params.has(CHAT_MSG_TO_PARAM)) return hash;
  params.delete(CHAT_MSG_TO_PARAM);
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
}

export function consumeChatMsgToFromWindow(win: Pick<Window, 'location' | 'history'> = window): string | null {
  const text = parseChatMsgToFromHash(win.location.hash);
  if (!text) return null;
  const nextHash = hashWithoutChatMsgTo(win.location.hash);
  if (nextHash !== win.location.hash) {
    win.history.replaceState(null, '', `${win.location.pathname}${win.location.search}${nextHash}`);
  }
  return text;
}

export function whenPageReady(callback: () => void): () => void {
  let cancelled = false;
  const run = () => {
    if (!cancelled) callback();
  };
  if (typeof document === 'undefined' || document.readyState === 'complete') {
    const id = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }
  const onLoad = () => window.setTimeout(run, 0);
  window.addEventListener('load', onLoad, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener('load', onLoad);
  };
}

export type ChatRole = 'user' | 'assistant';

export type ChatMessage = {
  role: ChatRole;
  content: string;
};

export type SuperContaChatSession = {
  uuid: string;
  messages: ChatMessage[];
};

export type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export type ChatPoster = (url: string, body?: unknown) => Promise<unknown>;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export function parseCreateUuid(data: unknown): string {
  if (typeof data === 'string') {
    const uuid = data.trim();
    if (uuid) return uuid;
  }
  const record = asRecord(data);
  const uuid = firstNonEmptyString(record?.uuid, record?.id, record?.trazabilidad);
  if (uuid) return uuid;
  throw new Error('No se recibió uuid de trazabilidad');
}

export type AssistantReply = {
  html: string;
  actions: string[];
};

export type SendChatResult = {
  session: SuperContaChatSession;
  actions: string[];
};

export type ActionRunner = (code: string) => void;

export function parseAssistantReply(data: unknown): AssistantReply {
  const record = asRecord(data);
  const html = firstNonEmptyString(record?.html);
  if (!html) {
    throw new Error('No se recibió respuesta de Super Conta');
  }
  const rawActions = record?.actions;
  const actions = Array.isArray(rawActions)
    ? rawActions.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : [];
  return { html, actions };
}

export function runChatActions(actions: string[], run: ActionRunner = executeAction): void {
  for (const action of actions) {
    const code = action.trim();
    if (code) run(code);
  }
}

function executeAction(code: string): void {
  // Instrucciones de Super Conta (asistente de primer partido).
  // eslint-disable-next-line no-new-func
  const result = new Function(`return (${code})`)();
  if (typeof result === 'function') result();
}

export function parseSuperContaStatus(data: unknown): boolean {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return (data as { ok?: unknown }).ok === true;
}

export function assistantReplyHtml(content: string): string | null {
  const trimmed = (content || '').trim();
  if (!/<\/?[a-z][\s\S]*>/i.test(trimmed)) return null;
  const body = trimmed.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const html = (body?.[1] ?? trimmed).trim();
  return html || null;
}

function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function loadChatSession(storage?: StorageLike | null): SuperContaChatSession | null {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return null;
  try {
    const raw = store.getItem(SUPERCONTA_CHAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SuperContaChatSession>;
    if (typeof parsed?.uuid !== 'string' || !parsed.uuid.trim()) return null;
    const messages = Array.isArray(parsed.messages)
      ? parsed.messages.filter(
          (msg): msg is ChatMessage =>
            !!msg &&
            (msg.role === 'user' || msg.role === 'assistant') &&
            typeof msg.content === 'string',
        )
      : [];
    return { uuid: parsed.uuid.trim(), messages };
  } catch {
    return null;
  }
}

export function saveChatSession(session: SuperContaChatSession, storage?: StorageLike | null): void {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return;
  try {
    store.setItem(SUPERCONTA_CHAT_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearChatSession(storage?: StorageLike | null): void {
  const store = storage === undefined ? defaultStorage() : storage;
  if (!store) return;
  try {
    store.removeItem(SUPERCONTA_CHAT_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

export async function ensureChatSession(
  poster: ChatPoster,
  storage?: StorageLike | null,
): Promise<SuperContaChatSession> {
  const existing = loadChatSession(storage);
  if (existing?.uuid) return existing;
  const uuid = parseCreateUuid(await poster('/api/chat/create'));
  const session: SuperContaChatSession = { uuid, messages: existing?.messages ?? [] };
  saveChatSession(session, storage);
  return session;
}

export async function sendChatMessage(
  poster: ChatPoster,
  content: string,
  storage?: StorageLike | null,
): Promise<SendChatResult> {
  const session = await ensureChatSession(poster, storage);
  const trimmed = content.trim();
  if (!trimmed) return { session, actions: [] };

  const messages: ChatMessage[] = [...session.messages, { role: 'user', content: trimmed }];
  const withUser: SuperContaChatSession = { uuid: session.uuid, messages };
  saveChatSession(withUser, storage);

  const payload: Record<string, unknown> = { uuid: session.uuid, messages };
  const context = getSuperContaContext();
  if (context) payload.context = context;
  const reply = parseAssistantReply(
    await poster('/api/chat/message', payload),
  );
  const next: SuperContaChatSession = {
    uuid: session.uuid,
    messages: [...messages, { role: 'assistant', content: reply.html }],
  };
  saveChatSession(next, storage);
  return { session: next, actions: reply.actions };
}
